import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { enqueueSnackbar } from "notistack";
import { JSX, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";

import type { Membership, Site } from "~/api/Api";
import { apiClient } from "~/api/ApiClient";
import styles from "~/components/teamMembers/TeamMembersTabs.module.scss";
import Modal from "~/components/ui/modal/Modal.tsx";
import { messageFromError } from "~/helpers/messageFromError.ts";
import { tryCatch } from "~/helpers/tryCatch";

import { Icon } from "../../assets/icons/Icon";
import FormLabel from "../login/formLabel/FormLabel";
import MultiSelect from "../mantine/multiSelect/MultiSelect.tsx";

type EditTeamMemberModalForm = {
  role: "admin" | "member";
  site_ids: string[];
};

type EditTeamMemberModalProps = {
  modalLabel: JSX.Element;
  closeModal: () => void;
  membership: Membership | null;
};

export const EditTeamMemberModal = ({
  modalLabel,
  closeModal,
  membership,
}: EditTeamMemberModalProps) => {
  const queryClient = useQueryClient();

  const { data: sites } = useQuery({
    enabled: !!membership,
    queryKey: ["sites"],
    queryFn: async () => {
      const response = await apiClient.sites.sitesList({
        "page[size]": 1000,
        s: "created_at ASC",
      });

      return response.data?.sites || [];
    },
    refetchOnWindowFocus: false,
  });

  const { mutate: promote, isPending: isPromoting } = useMutation({
    mutationFn: async () => {
      if (!membership) throw new Error("No membership");

      const result = await tryCatch(
        apiClient.memberships.promoteCreate(membership.id, {
          site_ids: [],
        }),
      );

      if (result.error) {
        const message = result.error || "Error promoting to Admin";
        enqueueSnackbar(messageFromError(message), {
          variant: "error",
          persist: true,
        });

        throw result.error;
      }

      queryClient.invalidateQueries({ queryKey: ["activeTeamMembers"] });
      enqueueSnackbar("Promoted to Admin", { variant: "success" });

      closeModal();
    },
  });

  const { mutate: demote, isPending: isDemoting } = useMutation({
    mutationFn: async (site_ids: number[]) => {
      if (!membership) throw new Error("No membership");

      const result = await tryCatch(
        apiClient.memberships.demoteCreate(membership.id, { site_ids }),
      );

      if (result.error) {
        const message = result.error || "Error demoting from Admin";
        enqueueSnackbar(messageFromError(message), {
          variant: "error",
          persist: true,
        });

        throw result.error;
      }

      queryClient.invalidateQueries({ queryKey: ["activeTeamMembers"] });
      enqueueSnackbar("Demoted from Admin", { variant: "success" });

      closeModal();
    },
  });

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { isDirty },
  } = useForm<EditTeamMemberModalForm>({
    defaultValues: {
      role: membership?.admin ? "admin" : "member",
      site_ids: [],
    },
  });

  // sync form with current membership
  useEffect(() => {
    if (!membership) {
      reset({
        role: "member",
        site_ids: [],
      });

      return;
    }

    const initialRole: "admin" | "member" = membership.admin
      ? "admin"
      : "member";

    // member + empty site_ids on backend = All sites
    const initialSiteIdsStrings: string[] =
      !membership.admin &&
      (!membership.site_ids || membership.site_ids.length === 0)
        ? ["all-sites"]
        : (membership.site_ids ?? []).map((id) => String(id));

    reset({
      role: initialRole,
      site_ids: initialSiteIdsStrings,
    });
  }, [membership]);

  const role = watch("role");
  const siteIdsValue = watch("site_ids");

  // normalized siteIds for backend request
  const numericSiteIds: number[] =
    role === "member"
      ? siteIdsValue.includes("all-sites")
        ? [] // All sites
        : siteIdsValue.map((id) => Number(id))
      : [];

  const isLoading = isPromoting || isDemoting;

  const onSubmit = ({ role }: EditTeamMemberModalForm) => {
    if (!membership) return;

    if (role === "admin") {
      promote();

      return;
    }

    demote(numericSiteIds);
  };

  const isDisabled =
    !membership || !isDirty || (role === "member" && siteIdsValue.length === 0);

  return (
    <Modal
      isOpen={!!membership}
      closeModal={closeModal}
      modalLabel={modalLabel}
      buttonLabel="Save Changes"
      isLoading={isLoading}
      formId="editTeamMember"
      isDisabled={isDisabled}
    >
      {membership && (
        <div key={membership.id} className={styles.inviteTeamMemberModal}>
          <p>
            Choose the role of the member of your team and pay attention to the
            difference in access in different roles
          </p>

          <div className={styles.roleButtons}>
            <div
              className={clsx(styles.roleButton, {
                [styles.roleButtonActive]: role === "admin",
              })}
              role="button"
              onClick={() => setValue("role", "admin", { shouldDirty: true })}
            >
              <div className={styles.roleButtonHeader}>
                <Icon iconName="AdminIcon" />
                <div>Admin</div>
              </div>
              <div className={styles.roleButtonHeaderDescription}>
                Admins can access all websites and their settings, view, and
                edit billing information and API keys. They have complete
                control over managing team members.
              </div>
            </div>

            <div
              className={clsx(styles.roleButton, {
                [styles.roleButtonActive]: role === "member",
              })}
              role="button"
              onClick={() => setValue("role", "member", { shouldDirty: true })}
            >
              <div className={styles.roleButtonHeader}>
                <Icon iconName="MemberIcon" />
                <div>Member</div>
              </div>
              <div className={styles.roleButtonHeaderDescription}>
                Members have limited access to website settings, can view team
                members but cannot edit them.
              </div>
            </div>
          </div>

          <form
            id="editTeamMember"
            onSubmit={handleSubmit(onSubmit)}
            className={styles.inviteTeamMemberForm}
          >
            {role === "member" && (
              <>
                <FormLabel
                  label="Site Access"
                  className={styles.selectContainer}
                />
                <Controller
                  name="site_ids"
                  control={control}
                  render={({ field }) => (
                    <MultiSelect
                      multiple
                      data={[
                        { value: "all-sites", label: "All sites" },
                        ...(sites?.map((site: Site) => ({
                          value: String(site?.id),
                          label: site?.fqdn,
                        })) || []),
                      ]}
                      searchable
                      clearable
                      {...field}
                      onChange={(values: string[]) => {
                        // add all-sites if it's included in new values
                        if (
                          values.includes("all-sites") &&
                          !field.value.includes("all-sites")
                        ) {
                          field.onChange(["all-sites"]);

                          return;
                        }

                        // remove all-sites if it was old value and now some other siteIds where selected
                        // (probably, it's not a real case)
                        if (
                          field.value.includes("all-sites") &&
                          values.length > 1
                        ) {
                          const specificSites = values.filter(
                            (value) => value !== "all-sites",
                          );
                          field.onChange(specificSites);

                          return;
                        }

                        field.onChange(values);
                      }}
                    />
                  )}
                />
              </>
            )}
          </form>
        </div>
      )}
    </Modal>
  );
};
